import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Dragging a clip: moving it along the track, or trimming either edge.
 *
 * Both gestures are the same mechanic — press, track the pointer in seconds,
 * commit — so they share one hook. The difference is only what the delta is
 * applied to: a move changes where the clip sits, a trim changes which part of
 * the source it plays.
 *
 * Deltas are reported against the position at press time rather than
 * accumulated per pointermove, so a drag that outruns the render loop lands
 * where the cursor is instead of drifting behind it.
 */

const SNAP_PX = 8;

export function useClipDrag({ pixelsPerSecond, getSnapTargets, onMove, onTrim, onCommit }) {
    const dragRef = useRef(null);
    const [dragging, setDragging] = useState(null);

    const snap = useCallback((seconds, exclusiveOf) => {
        const tolerance = SNAP_PX / pixelsPerSecond;
        const targets = getSnapTargets?.(exclusiveOf) || [];
        let best = null;
        let bestDistance = tolerance;
        targets.forEach((target) => {
            const distance = Math.abs(target - seconds);
            if (distance < bestDistance) {
                best = target;
                bestDistance = distance;
            }
        });
        return best === null ? seconds : best;
    }, [pixelsPerSecond, getSnapTargets]);

    const beginDrag = useCallback((event, clip, mode) => {
        if (event.button !== 0) return;
        event.preventDefault();
        event.stopPropagation();
        event.currentTarget.setPointerCapture?.(event.pointerId);

        dragRef.current = {
            pointerId: event.pointerId,
            mode,
            clipId: clip.id,
            startX: event.clientX,
            originStart: clip.start,
            originIn: clip.sourceIn,
            originOut: clip.sourceOut,
            lastApplied: 0,
            moved: false,
        };
        setDragging({ clipId: clip.id, mode });
    }, []);

    useEffect(() => {
        if (!dragging) return undefined;

        const handleMove = (event) => {
            const drag = dragRef.current;
            if (!drag) return;
            const deltaSeconds = (event.clientX - drag.startX) / pixelsPerSecond;
            if (Math.abs(event.clientX - drag.startX) > 2) drag.moved = true;

            if (drag.mode === 'move') {
                const raw = Math.max(0, drag.originStart + deltaSeconds);
                const duration = drag.originOut - drag.originIn;
                // Snap either edge of the clip, whichever lands first: butting a
                // clip's tail against the next one is as common as its head.
                const snappedStart = snap(raw, drag.clipId);
                const snappedEnd = snap(raw + duration, drag.clipId) - duration;
                const next = Math.abs(snappedStart - raw) <= Math.abs(snappedEnd - raw)
                    ? snappedStart
                    : snappedEnd;
                onMove(drag.clipId, Math.max(0, next));
                return;
            }

            // Trim is applied as an increment, so the reducer can clamp against
            // the source length without this hook needing to know it. Send only
            // what has not been applied yet.
            const edge = drag.mode === 'trim-left' ? 'left' : 'right';
            const anchor = edge === 'left' ? drag.originStart : drag.originStart + (drag.originOut - drag.originIn);
            const target = snap(anchor + deltaSeconds, drag.clipId);
            const desired = target - anchor;
            const increment = desired - drag.lastApplied;
            if (Math.abs(increment) < 1e-4) return;
            drag.lastApplied = desired;
            onTrim(drag.clipId, edge, increment);
        };

        const handleUp = () => {
            const drag = dragRef.current;
            dragRef.current = null;
            setDragging(null);
            if (drag?.moved) onCommit?.();
        };

        document.addEventListener('pointermove', handleMove);
        document.addEventListener('pointerup', handleUp);
        document.addEventListener('pointercancel', handleUp);
        return () => {
            document.removeEventListener('pointermove', handleMove);
            document.removeEventListener('pointerup', handleUp);
            document.removeEventListener('pointercancel', handleUp);
        };
    }, [dragging, pixelsPerSecond, snap, onMove, onTrim, onCommit]);

    return { beginDrag, dragging };
}
